import { useEffect, useState, useCallback } from 'react'
import { adminService } from 'core/services/adminService'
import { formatNumbers } from 'core/helpers/format-numbers'
import { UserTasksModal } from './user-tasks-modal'

import c from './assets/sass/user-details.module.scss'

const TX_LABELS = {
    EARN_TAP: 'Тапы',
    EARN_BATTLE: 'Выигрыш в битве',
    EARN_MINES: 'Выигрыш в минах',
    EARN_REFERRAL: 'Бонус за реферала',
    EARN_TASK: 'Награда за задание',
    EARN_ADVENT: 'Приз адвента',
    SPEND_BATTLE: 'Ставка в битве',
    SPEND_MINES: 'Ставка в минах',
    SPEND_CASE: 'Покупка кейса',
    REFUND_MINES: 'Возврат (мины)',
    REFUND_BATTLE: 'Возврат (битвы)',
    BONUS: 'Бонус',
}

export const UserDetails = ({ user: initialUser, onBack }) => {
    const [tab, setTab] = useState('info')
    const [user, setUser] = useState(null)
    const [transactions, setTransactions] = useState([])
    const [loading, setLoading] = useState(true)
    const [balanceAmount, setBalanceAmount] = useState('')
    const [balanceReason, setBalanceReason] = useState('')
    const [siteId, setSiteId] = useState('')
    const [referralName, setReferralName] = useState('')
    const [txFilter, setTxFilter] = useState('')
    const [showTasksModal, setShowTasksModal] = useState(false)

    const loadUserDetails = useCallback(async () => {
        try {
            const data = await adminService.getUserDetails(initialUser.id)
            setUser(data.user)
            setSiteId(data.user.siteId || '')
            setReferralName(data.user.referralDisplayName || '')
        } catch (error) {
            console.error('Failed to load user details:', error)
        } finally {
            setLoading(false)
        }
    }, [initialUser.id])

    const loadTransactions = useCallback(
        async (filter = '') => {
            try {
                const params = { limit: 50 }
                if (filter) params.type = filter
                const data = await adminService.getUserTransactions(
                    initialUser.id,
                    params
                )
                setTransactions(data.transactions)
            } catch (error) {
                console.error('Failed to load transactions:', error)
            }
        },
        [initialUser.id]
    )

    useEffect(() => {
        loadUserDetails()
    }, [loadUserDetails])

    useEffect(() => {
        if (tab === 'transactions') {
            loadTransactions(txFilter)
        }
    }, [tab, txFilter, loadTransactions])

    const handleBalanceUpdate = async () => {
        const amount = parseInt(balanceAmount)
        if (!amount || amount === 0) return alert('Введите сумму')
        try {
            await adminService.updateUserBalance(
                initialUser.id,
                amount,
                balanceReason || 'Admin adjustment'
            )
            setBalanceAmount('')
            setBalanceReason('')
            loadUserDetails()
            alert('Баланс обновлен!')
        } catch (error) {
            alert('Ошибка: ' + error.message)
        }
    }

    const handleSiteIdUpdate = async () => {
        // Очистка от невидимых символов
        const cleanedSiteId = siteId.trim().replace(/[^0-9]/g, '')

        if (!cleanedSiteId) {
            return alert('Введите корректный siteId (только цифры)')
        }

        if (cleanedSiteId.length < 3) {
            return alert('SiteId должен быть минимум 3 цифры')
        }

        try {
            await adminService.updateUserSiteId(initialUser.id, cleanedSiteId)
            loadUserDetails()
            alert('SiteId обновлен!')
        } catch (error) {
            const errorCode = error?.response?.data?.code
            const errorMsg = error?.response?.data?.error
            const existingUser = error?.response?.data?.existingUser

            if (errorCode === 'SITE_ID_TAKEN') {
                const ownerInfo =
                    existingUser?.username ||
                    existingUser?.firstName ||
                    existingUser?.id ||
                    'другим пользователем'
                alert(`Этот ID уже используется: ${ownerInfo}`)
            } else if (errorCode === 'INVALID_SITE_ID_FORMAT') {
                alert('Некорректный формат ID (только цифры)')
            } else {
                alert(
                    'Ошибка: ' +
                        (errorMsg || error.message || 'Неизвестная ошибка')
                )
            }
        }
    }

    const handleReferralNameUpdate = async () => {
        try {
            await adminService.updateReferralDisplayName(
                initialUser.id,
                referralName || null
            )
            loadUserDetails()
            alert('Реферальное имя обновлено!')
        } catch (error) {
            alert('Ошибка: ' + error.message)
        }
    }

    if (loading) return <div className={c.loading}>Загрузка...</div>
    if (!user) return <div className={c.error}>Ошибка загрузки</div>

    const avatar = user.photoUrl || '/images/avatar.png'
    const displayName = user.username || user.firstName || 'Пользователь'
    const firstLetter = displayName.charAt(0).toUpperCase()

    return (
        <div className={c.component}>
            <button className={c.back} onClick={onBack}>
                ← Назад к списку
            </button>

            <div className={c.header}>
                {user.photoUrl ? (
                    <img src={avatar} alt={displayName} className={c.avatar} />
                ) : (
                    <div className={c.avatarPlaceholder}>{firstLetter}</div>
                )}
                <div className={c.userInfo}>
                    <span className={c.name}>{displayName}</span>
                    <span className={c.tgId}>TG ID: {user.telegramId}</span>
                    <span className={c.balance}>
                        💰 {formatNumbers(parseInt(user.balance || 0))}
                    </span>
                </div>
            </div>

            <div className={c.tabs}>
                <button
                    className={tab === 'info' ? c.tabActive : c.tab}
                    onClick={() => setTab('info')}
                >
                    Информация
                </button>
                <button
                    className={tab === 'transactions' ? c.tabActive : c.tab}
                    onClick={() => setTab('transactions')}
                >
                    Транзакции
                </button>
                <button
                    className={tab === 'cases' ? c.tabActive : c.tab}
                    onClick={() => setTab('cases')}
                >
                    Кейсы
                </button>
            </div>

            {tab === 'info' && (
                <div className={c.content}>
                    <div className={c.field}>
                        <span className={c.label}>Site ID:</span>
                        <div className={c.inputGroup}>
                            <input
                                value={siteId || ''}
                                onChange={(e) => setSiteId(e.target.value)}
                                placeholder="Не установлен"
                            />
                            <button onClick={handleSiteIdUpdate}>
                                Сохранить
                            </button>
                        </div>
                    </div>
                    <div className={c.field}>
                        <span className={c.label}>Реферальное имя:</span>
                        <div className={c.inputGroup}>
                            <input
                                value={referralName || ''}
                                onChange={(e) =>
                                    setReferralName(e.target.value)
                                }
                                placeholder={
                                    user.username || 'По умолчанию username'
                                }
                            />
                            <button onClick={handleReferralNameUpdate}>
                                Сохранить
                            </button>
                        </div>
                    </div>
                    <div className={c.field}>
                        <span className={c.label}>Уровень:</span>
                        <span className={c.value}>LVL {user.level}</span>
                    </div>
                    <div className={c.field}>
                        <span className={c.label}>Опыт:</span>
                        <span className={c.value}>
                            {formatNumbers(parseInt(user.experience))}
                        </span>
                    </div>
                    <div className={c.field}>
                        <span className={c.label}>Энергия:</span>
                        <span className={c.value}>
                            {user.energy}/{user.maxEnergy}
                        </span>
                    </div>
                    <div className={c.field}>
                        <span className={c.label}>Рефералов:</span>
                        <span className={c.value}>{user.referralsCount}</span>
                    </div>
                    <div className={c.field}>
                        <span className={c.label}>Заработано с рефералки:</span>
                        <span className={c.value}>
                            {formatNumbers(
                                parseInt(user.referralEarnings || 0)
                            )}
                        </span>
                    </div>
                    <div className={c.field}>
                        <span className={c.label}>Выполнено заданий:</span>
                        <span
                            className={`${c.value} ${c.clickable}`}
                            onClick={() => setShowTasksModal(true)}
                        >
                            {user.completedTasks} 👁️
                        </span>
                    </div>
                    <div className={c.field}>
                        <span className={c.label}>Всего тапов:</span>
                        <span className={c.value}>
                            {formatNumbers(user.totalTaps)}
                        </span>
                    </div>
                    <div className={c.field}>
                        <span className={c.label}>Регистрация:</span>
                        <span className={c.value}>
                            {new Date(user.createdAt).toLocaleString('ru-RU')}
                        </span>
                    </div>
                    <div className={c.field}>
                        <span className={c.label}>Последняя активность:</span>
                        <span className={c.value}>
                            {new Date(user.lastActiveAt || user.lastLoginAt).toLocaleString('ru-RU')}
                        </span>
                    </div>

                    <div className={c.section}>
                        <h4>Изменить баланс</h4>
                        <input
                            type="number"
                            placeholder="Сумма (+ или -)"
                            value={balanceAmount || ''}
                            onChange={(e) => setBalanceAmount(e.target.value)}
                        />
                        <input
                            type="text"
                            placeholder="Причина (опционально)"
                            value={balanceReason || ''}
                            onChange={(e) => setBalanceReason(e.target.value)}
                        />
                        <button onClick={handleBalanceUpdate}>Применить</button>
                    </div>

                    <div className={c.section}>
                        <h4>Действия с аккаунтом</h4>
                        {user.isBanned ? (
                            <>
                                <div className={c.banInfo}>
                                    Забанен:{' '}
                                    {user.banReason || 'Причина не указана'}
                                </div>
                                <button
                                    className={c.btnSuccess}
                                    onClick={async () => {
                                        // eslint-disable-next-line no-restricted-globals
                                        if (!confirm('Разбанить пользователя?'))
                                            return
                                        try {
                                            await adminService.unbanUser(
                                                initialUser.id
                                            )
                                            loadUserDetails()
                                            alert('Пользователь разбанен!')
                                        } catch (error) {
                                            alert('Ошибка: ' + error.message)
                                        }
                                    }}
                                >
                                    Разбанить
                                </button>
                            </>
                        ) : (
                            <button
                                className={c.btnDanger}
                                onClick={async () => {
                                    // eslint-disable-next-line no-restricted-globals
                                    const reason = prompt('Причина бана:')
                                    if (!reason) return
                                    try {
                                        await adminService.banUser(
                                            initialUser.id,
                                            reason
                                        )
                                        loadUserDetails()
                                        alert('Пользователь забанен!')
                                    } catch (error) {
                                        alert('Ошибка: ' + error.message)
                                    }
                                }}
                            >
                                Забанить
                            </button>
                        )}
                    </div>
                </div>
            )}

            {tab === 'transactions' && (
                <div className={c.content}>
                    <div className={c.filter}>
                        <select
                            value={txFilter}
                            onChange={(e) => setTxFilter(e.target.value)}
                        >
                            <option value="">Все транзакции</option>
                            <option value="EARN_TAP">Тапы</option>
                            <option value="EARN_BATTLE">Выигрыши в битвах</option>
                            <option value="EARN_MINES">Выигрыши в минах</option>
                            <option value="EARN_REFERRAL">Бонусы за рефералов</option>
                            <option value="EARN_TASK">Награды за задания</option>
                            <option value="EARN_ADVENT">Призы адвента</option>
                            <option value="SPEND_BATTLE">Ставки в битвах</option>
                            <option value="SPEND_MINES">Ставки в минах</option>
                            <option value="SPEND_CASE">Покупка кейсов</option>
                            <option value="REFUND_MINES">Возвраты (мины)</option>
                            <option value="REFUND_BATTLE">Возвраты (битвы)</option>
                            <option value="BONUS">Бонусы</option>
                        </select>
                    </div>
                    {transactions.length === 0 ? (
                        <div className={c.empty}>Транзакций нет</div>
                    ) : (
                        <div className={c.transactions}>
                            {transactions.map((tx) => {
                                const isSpend = tx.type.startsWith('SPEND_')
                                const isPositive =
                                    tx.type.startsWith('EARN_') ||
                                    tx.type.startsWith('REFUND_') ||
                                    tx.type === 'BONUS'
                                const amount = parseInt(tx.amount)
                                const displayAmount = isSpend ? -amount : amount

                                return (
                                    <div key={tx.id} className={c.transaction}>
                                        <div className={c.txHeader}>
                                            <span className={c.txType}>
                                                {TX_LABELS[tx.type] || tx.type}
                                            </span>
                                            <span
                                                className={`${c.txAmount} ${isPositive ? c.positive : c.negative}`}
                                            >
                                                {displayAmount > 0 ? '+' : ''}
                                                {formatNumbers(displayAmount)}
                                            </span>
                                        </div>
                                        {tx.description && (
                                            <span className={c.txDesc}>
                                                {tx.description}
                                            </span>
                                        )}
                                        <span className={c.txDate}>
                                            {new Date(
                                                tx.createdAt
                                            ).toLocaleString('ru-RU')}
                                        </span>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            )}

            {tab === 'cases' && (
                <div className={c.content}>
                    {user.recentCases && user.recentCases.length > 0 ? (
                        <div className={c.cases}>
                            {user.recentCases.map((cs) => (
                                <div key={cs.id} className={c.caseItem}>
                                    <div className={c.caseHeader}>
                                        <span className={c.caseName}>
                                            {cs.caseName || 'Кейс'}
                                        </span>
                                        <span className={c.casePrice}>
                                            {formatNumbers(
                                                parseInt(cs.casePrice || 0)
                                            )}{' '}
                                            💰
                                        </span>
                                    </div>
                                    {cs.promoCode && (
                                        <span className={c.casePromo}>
                                            Промокод:{' '}
                                            <strong>{cs.promoCode}</strong>
                                        </span>
                                    )}
                                    <span className={c.caseDate}>
                                        {new Date(cs.createdAt).toLocaleString(
                                            'ru-RU'
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={c.empty}>Кейсы не открывались</div>
                    )}
                </div>
            )}

            {showTasksModal && (
                <UserTasksModal
                    userId={initialUser.id}
                    onClose={() => setShowTasksModal(false)}
                />
            )}
        </div>
    )
}
